import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, PencilSimple, UploadSimple } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Md } from '@/components/Md';
import { LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtDate } from '@/utils/dateFmt';
import { fmtNum } from '@/utils/numFmt';
import { PediatricGrowthPanel } from '@/components/admin/PediatricGrowthPanel';

const API = process.env.REACT_APP_BACKEND_URL;

function AvaliacaoTab({ patientId }) {
  const confirmDelete = useConfirmDelete();
  const [items, setItems] = useState([]);
  const [patient, setPatient] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], peso: '', altura: '', cb: '', pct: '', perimetros: {}, notes: '' });
  const [perimKey, setPerimKey] = useState('');
  const [perimVal, setPerimVal] = useState('');
  // T3 — Fase 5: programas adultos onde a altura é pré-preenchida do perfil
  const ADULT_PROGRAMS = ['gravida', 'pos_parto', 'pre_concecao', 'geral', 'emagrecimento'];
  const isAdult = ADULT_PROGRAMS.includes(patient?.patient_type) || (Array.isArray(patient?.patient_types) && patient.patient_types.some(t => ADULT_PROGRAMS.includes(t)));
  const defaultAltura = (isAdult && patient?.altura) ? String(patient.altura) : '';
  const fetch = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/patients/${patientId}/assessments`, { withCredentials: true }); setItems(data); } catch { }
  }, [patientId]);
  const fetchPatient = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/patients/${patientId}`, { withCredentials: true }); setPatient(data); } catch { }
  }, [patientId]);
  useEffect(() => { fetch(); fetchPatient(); }, [fetch, fetchPatient]);
  const addPerimetro = () => {
    if (!perimKey.trim()) return;
    setForm({ ...form, perimetros: { ...form.perimetros, [perimKey]: perimVal } });
    setPerimKey(''); setPerimVal('');
  };
  const removePerimetro = (k) => { const p = { ...form.perimetros }; delete p[k]; setForm({ ...form, perimetros: p }); };
  const calcIMC = () => {
    if (form.peso && form.altura) {
      try { return (parseFloat(form.peso) / Math.pow(parseFloat(form.altura) / 100, 2)).toFixed(1); } catch { return '-'; }
    }
    return '-';
  };
  const calcCMB = () => {
    const cb = parseFloat(form.cb);
    const pct = parseFloat(form.pct);
    if (cb > 0 && pct >= 0) {
      return (cb - (Math.PI * (pct / 10))).toFixed(1);
    }
    return '-';
  };
  const save = async () => {
    try {
      const payload = { ...form, peso: form.peso ? parseFloat(form.peso) : null, altura: form.altura ? parseFloat(form.altura) : null, cb: form.cb ? parseFloat(form.cb) : null, pct: form.pct ? parseFloat(form.pct) : null };
      if (editId) { await axios.put(`${API}/api/assessments/${editId}`, payload, { withCredentials: true }); }
      else { await axios.post(`${API}/api/patients/${patientId}/assessments`, payload, { withCredentials: true }); }
      toast.success('Avaliação guardada'); setShowForm(false); setEditId(null); fetch();
    } catch { toast.error('Erro'); }
  };
  const del = async (id) => {
    const it = items.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar a avaliação antropométrica de ${fmtDate(it?.date)}.` });
    if (!ok) return;
    await axios.delete(`${API}/api/assessments/${id}`, { withCredentials: true }); toast.success('Eliminada'); fetch();
  };
  const CMB_CLASS_COLORS = { 'Baixo peso': 'text-red-600 bg-red-50', 'Risco para baixo peso': 'text-amber-600 bg-amber-50', 'Eutrofia': 'text-green-600 bg-green-50', 'Sobrepeso': 'text-orange-600 bg-orange-50', 'Obesidade': 'text-red-700 bg-red-50' };
  return (
    <div data-testid="tab-avaliacao">
      <PediatricGrowthPanel patientId={patientId} refreshKey={`${items.length}-${items[0]?.id || ""}-${items[0]?.updated_at || ""}`} />
      <div className="flex justify-end mb-4">
        <button onClick={() => { setEditId(null); setForm({ date: new Date().toISOString().split('T')[0], peso: '', altura: defaultAltura, cb: '', pct: '', perimetros: {}, notes: '' }); setShowForm(true); }} className="bg-brand-green text-white rounded-full px-4 py-2 text-sm font-medium flex items-center gap-1" data-testid="add-assessment-btn"><Plus size={14} /> Nova avaliação</button>
      </div>
      {items.length === 0 ? <p className="text-center text-brand-muted py-8">Sem avaliações registadas.</p> : (
        <div className="space-y-3">
          {items.map(a => (
            <div key={a.id} className="bg-white border border-brand-border rounded-2xl p-4" data-testid={`assessment-${a.id}`}>
              <div className="flex items-start justify-between mb-2">
                <span className="text-xs font-medium text-brand-green bg-brand-mint/20 px-2 py-0.5 rounded-full">{fmtDate(a.date)}</span>
                <div className="flex gap-1">
                  <button onClick={() => { setEditId(a.id); setForm({ date: a.date, peso: a.peso || '', altura: a.altura || '', cb: a.cb || '', pct: a.pct || '', perimetros: a.perimetros || {}, notes: a.notes || '' }); setShowForm(true); }} className="p-1 text-brand-placeholder hover:text-brand-green" data-testid={`edit-assessment-${a.id}`}><PencilSimple size={14} /></button>
                  <button onClick={() => del(a.id)} className="p-1 text-brand-placeholder hover:text-red-500" data-testid={`delete-assessment-${a.id}`}><Trash size={14} /></button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><p className="text-xs text-brand-muted">Peso</p><p className="font-heading font-medium text-brand-text">{a.peso ? `${fmtNum(a.peso)} kg` : '-'}</p></div>
                <div><p className="text-xs text-brand-muted">Altura</p><p className="font-heading font-medium text-brand-text">{a.altura ? `${fmtNum(a.altura)} cm` : '-'}</p></div>
                <div><p className="text-xs text-brand-muted">IMC</p><p className="font-heading font-medium text-brand-text">{a.imc ? fmtNum(a.imc) : '-'}</p></div>
              </div>
              {(a.cb || a.cmb) && (
                <div className="grid grid-cols-3 gap-3 mt-2 pt-2 border-t border-brand-border/50">
                  <div><p className="text-xs text-brand-muted">CB</p><p className="font-heading font-medium text-brand-text">{a.cb ? `${fmtNum(a.cb)} cm` : '-'}</p></div>
                  <div><p className="text-xs text-brand-muted">PCT</p><p className="font-heading font-medium text-brand-text">{a.pct ? `${fmtNum(a.pct)} mm` : '-'}</p></div>
                  <div>
                    <p className="text-xs text-brand-muted">CMB</p>
                    <p className="font-heading font-medium text-brand-text">{a.cmb ? `${fmtNum(a.cmb)} cm` : '-'}</p>
                    {a.cmb_classification && <span className={`text-[9px] px-2 py-0.5 rounded-full ${CMB_CLASS_COLORS[a.cmb_classification] || 'bg-brand-bg text-brand-muted'}`}>{a.cmb_classification}</span>}
                  </div>
                </div>
              )}
              {a.perimetros && Object.keys(a.perimetros).length > 0 && (
                <div className="mt-2 pt-2 border-t border-brand-border/50">
                  <p className="text-[10px] text-brand-muted uppercase tracking-wider mb-1">Perímetros</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(a.perimetros).map(([k, v]) => (
                      <span key={k} className="text-xs bg-brand-bg px-2 py-0.5 rounded-full">{k}: {fmtNum(v)} cm</span>
                    ))}
                  </div>
                </div>
              )}
              {a.notes && <Md as="p" className="text-xs text-brand-muted mt-2">{a.notes}</Md>}
            </div>
          ))}
        </div>
      )}
      {/* CMB Evolution */}
      {items.some(a => a.cmb) && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mt-4" data-testid="cmb-evolution">
          <p className="text-[10px] text-brand-muted uppercase tracking-wider mb-2">Evolução da CMB</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-brand-border">
                <th className="text-left py-1.5 text-brand-muted font-medium">Data</th>
                <th className="text-center py-1.5 text-brand-muted font-medium">CB (cm)</th>
                <th className="text-center py-1.5 text-brand-muted font-medium">PCT (mm)</th>
                <th className="text-center py-1.5 text-brand-muted font-medium">CMB (cm)</th>
                <th className="text-center py-1.5 text-brand-muted font-medium">Classificação</th>
              </tr></thead>
              <tbody>
                {items.filter(a => a.cmb).map(a => (
                  <tr key={a.id} className="border-b border-brand-border/30">
                    <td className="py-1.5 text-brand-text">{fmtDate(a.date)}</td>
                    <td className="py-1.5 text-center text-brand-text">{a.cb ? fmtNum(a.cb) : '-'}</td>
                    <td className="py-1.5 text-center text-brand-text">{a.pct ? fmtNum(a.pct) : '-'}</td>
                    <td className="py-1.5 text-center font-medium text-brand-green">{fmtNum(a.cmb)}</td>
                    <td className="py-1.5 text-center"><span className={`text-[9px] px-2 py-0.5 rounded-full ${CMB_CLASS_COLORS[a.cmb_classification] || 'bg-brand-bg text-brand-muted'}`}>{a.cmb_classification || '-'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editId ? 'Editar avaliação' : 'Nova avaliação'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Data</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-xl border-brand-border" data-testid="assessment-date" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs text-brand-muted mb-1 block">Peso (kg)</Label><Input type="number" step="0.1" value={form.peso} onChange={e => setForm({ ...form, peso: e.target.value })} className="rounded-xl border-brand-border" data-testid="assessment-peso" /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Altura (cm)</Label><Input type="number" step="0.1" value={form.altura} onChange={e => setForm({ ...form, altura: e.target.value })} className="rounded-xl border-brand-border" data-testid="assessment-altura" /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">IMC</Label><p className="text-sm font-medium text-brand-green mt-2">{fmtNum(calcIMC())}</p></div>
            </div>
            {/* CMB: Circunferência Muscular do Braço */}
            <div className="border-t border-brand-border/50 pt-3">
              <p className="text-[10px] text-brand-muted uppercase tracking-wider mb-2">Circunferência Muscular do Braço</p>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs text-brand-muted mb-1 block">CB (cm)</Label><Input type="number" step="0.1" value={form.cb} onChange={e => setForm({ ...form, cb: e.target.value })} className="rounded-xl border-brand-border" placeholder="Circ. braço" data-testid="assessment-cb" /></div>
                <div><Label className="text-xs text-brand-muted mb-1 block">PCT (mm)</Label><Input type="number" step="0.1" value={form.pct} onChange={e => setForm({ ...form, pct: e.target.value })} className="rounded-xl border-brand-border" placeholder="Prega tricipital" data-testid="assessment-pct" /></div>
                <div><Label className="text-xs text-brand-muted mb-1 block">CMB (cm)</Label><p className="text-sm font-medium text-brand-green mt-2" data-testid="cmb-result">{fmtNum(calcCMB())}</p></div>
              </div>
              <p className="text-[9px] text-brand-placeholder mt-1">CMB = CB - (π x PCT/10). Classificação por Frisancho (1990).</p>
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Perímetros (cm)</Label>
              <div className="flex flex-wrap gap-1 mb-2">
                {Object.entries(form.perimetros).map(([k, v]) => (
                  <span key={k} className="text-xs bg-brand-bg px-2 py-1 rounded-full flex items-center gap-1">{k}: {v} <button onClick={() => removePerimetro(k)} className="text-red-400">&times;</button></span>
                ))}
              </div>
              <div className="flex gap-1.5">
                <Input value={perimKey} onChange={e => setPerimKey(e.target.value)} placeholder="Nome (ex: cintura)" className="flex-1 rounded-xl border-brand-border text-xs" />
                <Input value={perimVal} onChange={e => setPerimVal(e.target.value)} placeholder="cm" className="w-16 rounded-xl border-brand-border text-xs" />
                <button onClick={addPerimetro} className="text-brand-green text-xs font-medium px-2">+</button>
              </div>
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Notas</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="rounded-xl border-brand-border" rows={2} /></div>
            <button onClick={save} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="save-assessment-btn">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AvaliacaoTab;
